import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import { useContext, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { CampaignContext } from "../Context";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import CustomDropzone from "global/components/CustomDropzone/CustomDropzone";
import {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import DropZoneIcon from "assets/icons1/dropzone_icon.svg";
import { attachFileUpload } from "screens/Campaigns/CampaignService";
import strings from "global/constants/StringConstants";

const AttachmentSection = () => {
  const {
    submitCampaign,
    setSubmitCampaign,
    managingStep,
    setManagingStep,
    saveAsDraft,
  } = useContext(CampaignContext);
  const acceptedFiles = [
    strings.doc,
    strings.docx,
    strings.txt,
    strings.pdf,
    strings.png,
    strings.jpeg,
    strings.jpg,
    strings.gif,
    strings.xlsx,
    strings.xls,
    // strings.bmp,
    // strings.tiff,
    strings.csv,
  ];
  const [attachmentFileSize, setAttachmentFileSize] = useState<any>([]);
  const [totalAttachmentSize, setTotalAttachmentSize] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [formFields, setFormFields] = useState({
    attachments: {
      value: submitCampaign.campaign.attachments ?? {},
      error: "",
    },
  });
  const attachmentNames = Object.keys(formFields.attachments);

  const handleSave = async () => {
    const campaign = {
      ...submitCampaign,
      campaign: {
        ...submitCampaign.campaign,
        attachments: formFields.attachments.value,
      },
    };
    setSubmitCampaign(campaign);
    setSaving(true);
    await saveAsDraft(campaign);
    setManagingStep("");
    setSaving(false);
  };

  const handleCancel = () => {
    setManagingStep("");
    setFormFields({
      attachments: {
        value: submitCampaign.campaign.attachments ?? {},
        error: "",
      },
    });
  };

  const dropzoneUploadFile = async (event: File[]) => {
    const fileSizeArray: any[] = [...attachmentFileSize];
    const rejectedFiles: string[] = [];
    const individualOversizedRejectedFile: string[] = [];

    try {
      setSaving(true);
      let sizeTotal = totalAttachmentSize;
      let newAttachments = { ...formFields.attachments.value };
      const uniqueAttachments = event.filter(
        (attachment) => !attachmentNames.includes(attachment.name)
      );
      checkDuplicateAttachments(event);

      for (const attachment of uniqueAttachments) {
        const attachmentSize = attachment.size;
        const formData = new FormData();
        formData.append("file", attachment);

        switch (true) {
          case attachmentSize > 10_000_000:
            individualOversizedRejectedFile.push(attachment.name);
            break;
          case sizeTotal + attachmentSize <= 20_000_000:
            const response = await attachFileUpload(formData);
            sizeTotal += attachmentSize;
            newAttachments[attachment.name] = response.message;
            fileSizeArray.push({ name: attachment.name, size: attachmentSize });
            break;
          default:
            rejectedFiles.push(attachment.name);
        }
      }
      setAttachmentFileSize(fileSizeArray);
      setTotalAttachmentSize(sizeTotal);
      setFormFields((prevFields) => ({
        ...prevFields,
        attachments: {
          ...prevFields.attachments,
          value: newAttachments,
        },
      }));
      setSaving(false);
      rejectedFilesHandler(rejectedFiles, individualOversizedRejectedFile);
    } catch (error: any) {
      setSaving(false);
      handleErrorMessage(error);
    } finally {
      setSaving(false);
    }
  };

  const checkDuplicateAttachments = (attachments: any) => {
    const existingAttachmentNames = new Set(attachmentNames);
    const duplicateFileNames = attachments
      .map((attachment: any) => attachment.name)
      .filter((name: any) => existingAttachmentNames.has(name));

    if (duplicateFileNames.length > 0) {
      openErrorNotification(
        `${duplicateFileNames.join(", ")} ${
          duplicateFileNames.length > 1 ? "are" : "is"
        } already uploaded`
      );
    }
  };

  const handleDeleteAttachment = (fileName: string) => {
    setFormFields((prevFields) => {
      const updatedAttachments = { ...prevFields.attachments.value };
      delete updatedAttachments[fileName];
      return {
        ...prevFields,
        attachments: {
          ...prevFields.attachments,
          value: updatedAttachments,
        },
      };
    });
  };

  const getFileRejectionErrorMessage = (
    rejectedAttachments: string[],
    individualOversizedAttachments: string[]
  ) => {
    let errorMessage: string = "";
    const rejectedIndividualFileNames: string =
      individualOversizedAttachments.join(", ");
    const rejectedFileNames: string = rejectedAttachments.join(", ");

    switch (true) {
      case rejectedAttachments.length > 0 &&
        individualOversizedAttachments.length > 0: {
        errorMessage = `${rejectedIndividualFileNames} individually exceed the 10 mb limit and ${rejectedFileNames} altogether exceeds the 20 mb limit for attachment upload size.`;
        break;
      }
      case rejectedAttachments.length > 0: {
        errorMessage = `${rejectedFileNames} exceed the 20 mb limit for attachment upload size.`;
        break;
      }
      case individualOversizedAttachments.length > 0: {
        errorMessage = `${individualOversizedAttachments} individually exceed the 10 mb limit for attachment upload size.`;
        break;
      }
    }
    return errorMessage;
  };

  const rejectedFilesHandler = (
    rejectedAttachments: string[],
    individualOversizedAttachments: string[]
  ) => {
    const errorMessage = getFileRejectionErrorMessage(
      rejectedAttachments,
      individualOversizedAttachments
    );
    if (isTruthy(errorMessage)) {
      openErrorNotification(errorMessage);
    }
  };

  const stepCompletion = () => {
    return isTruthy(submitCampaign.campaign.attachments);
  };

  const getExpandedView = () => (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" spacing={4} width="100%">
          <Stack spacing={2} width="100%">
            <Stack spacing={0.5}>
              <Typography variant="h4">Attachments</Typography>
              <Typography variant="body1">
                Upload files like PDFs or images if needed
              </Typography>
            </Stack>
            <Grid container>
              <Grid item xs={12}>
                <Stack display={"flex"} flexDirection={"column"}>
                  <CustomDropzone
                    sx={{ height: "6rem" }}
                    onAdd={dropzoneUploadFile}
                    maxFileSize={20 * 1024 * 1024}
                    acceptedFiles={acceptedFiles}
                    showAlerts={false}
                    multipleFileUpload={true}
                    icon={DropZoneIcon}
                    dropzoneText="Choose a file or drag & drop it here"
                    fileExtenstionText={`Attachments should be up to 10 MB individually and 20 MB altogether.`}
                  />
                  <Typography
                    variant="subtitle2"
                    color="#FF9436"
                    pt={1}
                    sx={{ opacity: 0.9, display: "flex", alignItems: "center" }}
                  >
                    {`The files should be in ${acceptedFiles
                      ?.map((file) => file.replace(".", "").toLowerCase())
                      .join(
                        ", "
                      )} and the size should not exceed 10 MB individually and 20 MB altogether.`}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
            <Grid container gap={1}>
              {Object.keys(formFields.attachments.value).map((fileName) => (
                <Chip
                  key={fileName}
                  label={fileName}
                  onDelete={() => handleDeleteAttachment(fileName)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Grid>
          </Stack>
        </Stack>
        <Stack>
          <IconButton onClick={handleCancel}>
            <SvgIcon>
              <CloseIcon />
            </SvgIcon>
          </IconButton>
        </Stack>
      </Stack>
      <Stack
        alignItems="center"
        justifyContent="flex-end"
        direction="row"
        spacing={1}
      >
        <Button size="small" variant="text" onClick={handleCancel}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={handleSave}>
          Save
        </Button>
      </Stack>
    </Stack>
  );

  const getNonExpandedView = () => {
    const onAnotherStep = managingStep !== "" && managingStep !== "attachments";
    return (
      <Grid container gap={1} sx={{ opacity: onAnotherStep ? 0.5 : 1 }}>
        <Stack
          direction="row"
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          <Stack spacing={0.5} pb={{ xs: 1 }}>
            <Typography variant="h4">Attachments</Typography>
            <Typography variant="body1">
              {Object.keys(formFields.attachments.value).length > 0
                ? "The following files will be included as attachments with the campaign"
                : "Upload files like PDFs or images if needed"}
            </Typography>
          </Stack>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setManagingStep("attachments")}
            disabled={onAnotherStep}
            sx={{
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {`${stepCompletion() ? "Edit" : "Add"}`} Attachments
          </Button>
        </Stack>
        {Object.keys(formFields.attachments.value).length > 0 && (
          <Stack pt={1}>
            <Grid container gap={1}>
              {Object.keys(formFields.attachments.value).map((fileName) => (
                <Chip
                  key={fileName}
                  label={fileName}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Grid>
          </Stack>
        )}
      </Grid>
    );
  };

  return (
    <>
      <Box px={3}>
        {managingStep === "attachments"
          ? getExpandedView()
          : getNonExpandedView()}
      </Box>
      <CustomLoader isLoading={saving} />
    </>
  );
};

export default AttachmentSection;
